import { Injectable } from '@angular/core';
import { ApiClient } from './api-client';
import {
  AIDiagramRequest,
  AIDiagramResponse,
  ApiRequestOptions,
  ArchiveListStatus,
  Diagram,
  DiagramCreate,
  DiagramLinksResponse,
  DiagramSortBy,
  DiagramUpdate,
  Folder,
  FolderCreate,
  FolderUpdate,
  AuthProviders,
  AuthSession,
  LicenseNotice,
  LicenseOverview,
  MemberInviteResult,
  Organization,
  OrganizationUpdate,
  OrgMember,
  OrgRole,
  CsrfToken,
  GoogleAuthRequest,
  LoginChallenge,
  LoginMfaRequest,
  LogoutResponse,
  MfaTokenRequest,
  PasswordLoginRequest,
  RegistrationPreview,
  TotpStartResponse,
  PasskeyInfo,
  WebAuthnRegisterVerifyRequest,
  PaginatedResponse,
  Project,
  ProjectCreate,
  ProjectMember,
  ProjectMemberAdd,
  ProjectMemberUpdate,
  ProjectSortBy,
  ProjectUpdate,
  QueryParams,
  SearchEntityType,
  SearchResult,
  ShareCreate,
  ShareLink,
  ShareList,
  ShareRevokeResponse,
  ShareUpdate,
  SortOrder,
  RefreshResult,
  WebAuthnLoginVerifyRequest,
  User,
  UUID,
  Version,
} from './models';

/**
 * BPMN Workspace API v2 client.
 * Base URL: `environment.apiUrl` → `/api/v2`.
 */
@Injectable({
  providedIn: 'root',
})
export class AppRepository {
  constructor(protected api: ApiClient) {}

  // -------------------------
  // Auth
  // -------------------------
  authGoogle(body: GoogleAuthRequest, options: ApiRequestOptions = {}) {
    return this.post<LoginChallenge>('/auth/google', body, options);
  }

  csrf(options: ApiRequestOptions = {}) {
    return this.api.get<CsrfToken>('/auth/csrf', options);
  }

  /** Current user plus access-token lifetime. Tokens themselves stay in cookies. */
  session(options: ApiRequestOptions = {}) {
    return this.api.get<AuthSession>('/auth/session', options);
  }

  getMe(options: ApiRequestOptions = {}) {
    return this.api.get<User>('/auth/me', options);
  }

  refreshToken(options: ApiRequestOptions = {}) {
    return this.post<RefreshResult>('/auth/refresh', {}, options);
  }

  logout(options: ApiRequestOptions = {}) {
    return this.post<LogoutResponse>('/auth/logout', {}, options);
  }

  deleteAccount(options: ApiRequestOptions = {}) {
    return this.api.delete<LogoutResponse>('/auth/me', options);
  }

  updateProfile(fullName: string, options: ApiRequestOptions = {}) {
    return this.patch<User>('/auth/me', { full_name: fullName }, options);
  }

  changePassword(
    body: { new_password: string; current_password?: string },
    options: ApiRequestOptions = {},
  ) {
    return this.post<{ status: string }>('/auth/password', body, options);
  }

  startProfileTotp(options: ApiRequestOptions = {}) {
    return this.post<TotpStartResponse>('/auth/2fa/totp/start', {}, options);
  }

  confirmProfileTotp(body: { code: string; current_password?: string }, options: ApiRequestOptions = {}) {
    return this.post<{ status: string }>('/auth/2fa/totp/confirm', body, options);
  }

  disableProfileTotp(currentPassword?: string, options: ApiRequestOptions = {}) {
    return this.post<{ status: string }>('/auth/2fa/totp/disable', stepUp(currentPassword), options);
  }

  startProfileEmail2fa(options: ApiRequestOptions = {}) {
    return this.post<{ status: string }>('/auth/2fa/email/start', {}, options);
  }

  confirmProfileEmail2fa(body: { code: string; current_password?: string }, options: ApiRequestOptions = {}) {
    return this.post<{ status: string }>('/auth/2fa/email/confirm', body, options);
  }

  disableProfileEmail2fa(currentPassword?: string, options: ApiRequestOptions = {}) {
    return this.post<{ status: string }>('/auth/2fa/email/disable', stepUp(currentPassword), options);
  }

  profileWebauthnOptions(options: ApiRequestOptions = {}) {
    return this.post<Record<string, unknown>>('/auth/2fa/webauthn/register/options', {}, options);
  }

  profileWebauthnVerify(
    body: { credential: Record<string, unknown>; name?: string; current_password?: string },
    options: ApiRequestOptions = {},
  ) {
    return this.post<PasskeyInfo>('/auth/2fa/webauthn/register/verify', body, options);
  }

  deleteProfileWebauthn(id: string, currentPassword?: string, options: ApiRequestOptions = {}) {
    return this.api.delete<{ status: string }>(`/auth/2fa/webauthn/${id}`, {
      ...options,
      json: stepUp(currentPassword),
    });
  }

  getOrganization(options: ApiRequestOptions = {}) {
    return this.api.get<Organization>('/organization', options);
  }

  updateOrganization(body: OrganizationUpdate, options: ApiRequestOptions = {}) {
    return this.patch<Organization>('/organization', body, options);
  }

  resetOrganization(options: ApiRequestOptions = {}) {
    return this.post<Organization>('/organization/reset', {}, options);
  }

  organizationLogo(options: ApiRequestOptions = {}) {
    return this.api.getBlob('/organization/logo', options);
  }

  uploadOrganizationLogo(file: File, options: ApiRequestOptions = {}) {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.multipart<Organization>('/organization/logo', form, options);
  }

  deleteOrganizationLogo(options: ApiRequestOptions = {}) {
    return this.delete<Organization>('/organization/logo', options);
  }

  listMembers(options: ApiRequestOptions = {}) {
    return this.api.get<OrgMember[]>('/organization/members', options);
  }

  inviteMember(
    body: { email: string; full_name: string; role: OrgRole },
    options: ApiRequestOptions = {},
  ) {
    return this.post<MemberInviteResult>('/organization/members', body, options);
  }

  resendInvite(id: UUID, options: ApiRequestOptions = {}) {
    return this.post<MemberInviteResult>(`/organization/invites/${id}/resend`, {}, options);
  }

  deleteInvite(id: UUID, options: ApiRequestOptions = {}) {
    return this.delete<{ status: string }>(`/organization/invites/${id}`, options);
  }

  setMemberActive(id: UUID, isActive: boolean, options: ApiRequestOptions = {}) {
    return this.patch<OrgMember>(`/organization/members/${id}`, { is_active: isActive }, options);
  }

  setMemberRole(id: UUID, role: OrgRole, options: ApiRequestOptions = {}) {
    return this.patch<OrgMember>(`/organization/members/${id}`, { role }, options);
  }

  setMemberName(id: UUID, fullName: string, options: ApiRequestOptions = {}) {
    return this.patch<OrgMember>(`/organization/members/${id}`, { full_name: fullName }, options);
  }

  deleteMember(id: UUID, options: ApiRequestOptions = {}) {
    return this.delete<{ status: string }>(`/organization/members/${id}`, options);
  }

  licenseNotice(options: ApiRequestOptions = {}) {
    return this.api.get<LicenseNotice>('/license/notice', options);
  }

  licenseOverview(options: ApiRequestOptions = {}) {
    return this.api.get<LicenseOverview>('/license', options);
  }

  authProviders(options: ApiRequestOptions = {}) {
    return this.api.get<AuthProviders>('/auth/providers', options);
  }

  loginWithPassword(body: PasswordLoginRequest, options: ApiRequestOptions = {}) {
    return this.post<LoginChallenge>('/auth/login', body, options);
  }

  requestPasswordReset(email: string, options: ApiRequestOptions = {}) {
    return this.post<{ status: string }>('/auth/password/forgot', { email }, options);
  }

  resetPassword(token: string, password: string, options: ApiRequestOptions = {}) {
    return this.post<{ status: string }>('/auth/password/reset', { token, password }, options);
  }

  loginMfa(body: LoginMfaRequest, options: ApiRequestOptions = {}) {
    return this.post<AuthSession>('/auth/login/2fa', body, options);
  }

  sendLoginEmailCode(body: MfaTokenRequest, options: ApiRequestOptions = {}) {
    return this.post<{ status: string }>('/auth/login/2fa/email/send', body, options);
  }

  loginWebauthnOptions(body: MfaTokenRequest, options: ApiRequestOptions = {}) {
    return this.post<Record<string, unknown>>('/auth/login/webauthn/options', body, options);
  }

  loginWebauthnVerify(body: WebAuthnLoginVerifyRequest, options: ApiRequestOptions = {}) {
    return this.post<AuthSession>('/auth/login/webauthn/verify', body, options);
  }

  startLoginTotp(body: MfaTokenRequest, options: ApiRequestOptions = {}) {
    return this.post<TotpStartResponse>('/auth/enroll/totp/start', body, options);
  }

  confirmLoginTotp(body: { mfa_token: string; code: string }, options: ApiRequestOptions = {}) {
    return this.post<AuthSession>('/auth/enroll/totp/confirm', body, options);
  }

  startLoginEmailEnrollment(body: MfaTokenRequest, options: ApiRequestOptions = {}) {
    return this.post<{ status: string }>('/auth/enroll/email/start', body, options);
  }

  confirmLoginEmailEnrollment(body: { mfa_token: string; code: string }, options: ApiRequestOptions = {}) {
    return this.post<AuthSession>('/auth/enroll/email/confirm', body, options);
  }

  loginEnrollWebauthnOptions(body: MfaTokenRequest, options: ApiRequestOptions = {}) {
    return this.post<Record<string, unknown>>('/auth/enroll/webauthn/options', body, options);
  }

  loginEnrollWebauthnVerify(body: WebAuthnLoginVerifyRequest, options: ApiRequestOptions = {}) {
    return this.post<AuthSession>('/auth/enroll/webauthn/verify', body, options);
  }

  registrationPreview(token: string, options: ApiRequestOptions = {}) {
    return this.api.get<RegistrationPreview>(registerPath(token), options);
  }

  setRegistrationPassword(token: string, password: string, options: ApiRequestOptions = {}) {
    return this.post<{ status: string }>(`${registerPath(token)}/password`, { password }, options);
  }

  finishRegistration(token: string, options: ApiRequestOptions = {}) {
    return this.post<AuthSession>(`${registerPath(token)}/finish`, {}, options);
  }

  startRegistrationTotp(token: string, options: ApiRequestOptions = {}) {
    return this.post<TotpStartResponse>(`${registerPath(token)}/2fa/totp/start`, undefined, options);
  }

  confirmRegistrationTotp(token: string, code: string, options: ApiRequestOptions = {}) {
    return this.post<AuthSession>(`${registerPath(token)}/2fa/totp/confirm`, { code }, options);
  }

  startRegistrationEmail(token: string, options: ApiRequestOptions = {}) {
    return this.post<{ status: string }>(`${registerPath(token)}/2fa/email/start`, undefined, options);
  }

  confirmRegistrationEmail(token: string, code: string, options: ApiRequestOptions = {}) {
    return this.post<AuthSession>(`${registerPath(token)}/2fa/email/confirm`, { code }, options);
  }

  registrationWebauthnOptions(token: string, options: ApiRequestOptions = {}) {
    return this.post<Record<string, unknown>>(`${registerPath(token)}/2fa/webauthn/options`, undefined, options);
  }

  confirmRegistrationWebauthn(token: string, body: WebAuthnRegisterVerifyRequest, options: ApiRequestOptions = {}) {
    return this.post<AuthSession>(`${registerPath(token)}/2fa/webauthn/verify`, body, options);
  }

  // -------------------------
  // Projects
  // -------------------------
  readProjects(
    params?: {
      skip?: number;
      limit?: number;
      sort_by?: ProjectSortBy;
      sort_order?: SortOrder;
    },
    options: Omit<ApiRequestOptions, 'query'> = {},
  ) {
    return this.get<PaginatedResponse<Project>>('/projects/', params, options);
  }

  createProject(body: ProjectCreate, options: ApiRequestOptions = {}) {
    return this.post<Project>('/projects/', body, options);
  }

  readProject(id: UUID, options: ApiRequestOptions = {}) {
    return this.api.get<Project>(`/projects/${id}`, options);
  }

  updateProject(id: UUID, body: ProjectUpdate, options: ApiRequestOptions = {}) {
    return this.put<Project>(`/projects/${id}`, body, options);
  }

  getMembers(id: UUID, options: ApiRequestOptions = {}) {
    return this.api.get<ProjectMember[]>(`/projects/${id}/members`, options);
  }

  addMember(id: UUID, body: ProjectMemberAdd, options: ApiRequestOptions = {}) {
    return this.post<ProjectMember>(`/projects/${id}/members`, body, options);
  }

  updateMember(id: UUID, body: ProjectMemberUpdate, options: ApiRequestOptions = {}) {
    return this.api.put<ProjectMember>(`/projects/${id}/members`, { ...options, json: body });
  }

  removeMember(id: UUID, email: string, options: ApiRequestOptions = {}) {
    return this.api.delete<{ status: string }>(`/projects/${id}/members`, {
      ...options,
      query: { email },
    });
  }

  // -------------------------
  // Folders
  // -------------------------
  readFolders(
    projectId: UUID,
    params?: { status?: ArchiveListStatus },
    options: ApiRequestOptions = {},
  ) {
    return this.api.get<Folder[]>(`/folders/project/${projectId}`, {
      ...options,
      query: params?.status ? { status: params.status } : undefined,
    });
  }

  createFolder(body: FolderCreate, options: ApiRequestOptions = {}) {
    return this.post<Folder>('/folders/', body, options);
  }

  updateFolder(id: UUID, body: FolderUpdate, options: ApiRequestOptions = {}) {
    return this.put<Folder>(`/folders/${id}`, body, options);
  }

  addFolderMember(folderId: UUID, body: ProjectMemberAdd, options: ApiRequestOptions = {}) {
    return this.post<ProjectMember>(`/folders/${folderId}/members`, body, options);
  }

  moveFolder(folderId: UUID, projectId: UUID, options: ApiRequestOptions = {}) {
    return this.post<Folder>(`/folders/${folderId}/move`, { project_id: projectId }, options);
  }

  deleteFolder(folderId: UUID, options: ApiRequestOptions = {}) {
    return this.delete<{ status: string }>(`/folders/${folderId}`, options);
  }

  // -------------------------
  // Diagrams
  // -------------------------
  readDiagrams(
    projectId: UUID,
    params?: {
      /** Omit = all folders; `null` = uncategorized (`folder_id=` / `""`). */
      folder_id?: UUID | null;
      skip?: number;
      limit?: number;
      sort_by?: DiagramSortBy;
      sort_order?: SortOrder;
      status?: ArchiveListStatus;
    },
    options: Omit<ApiRequestOptions, 'query'> = {},
  ) {
    return this.get<PaginatedResponse<Diagram>>(
      `/diagrams/project/${projectId}`,
      diagramsQuery(params),
      options,
    );
  }

  createDiagram(body: DiagramCreate, options: ApiRequestOptions = {}) {
    return this.post<Diagram>('/diagrams/', body, options);
  }

  readDiagram(id: UUID, options: ApiRequestOptions = {}) {
    return this.api.get<Diagram>(`/diagrams/detail/${id}`, options);
  }

  readDiagramLinks(id: UUID, options: ApiRequestOptions = {}) {
    return this.api.get<DiagramLinksResponse>(`/diagrams/${id}/links`, options);
  }

  updateDiagram(id: UUID, body: DiagramUpdate, options: ApiRequestOptions = {}) {
    return this.put<Diagram>(`/diagrams/${id}`, body, options);
  }

  aiEditDiagram(body: AIDiagramRequest, options: ApiRequestOptions = {}) {
    return this.post<AIDiagramResponse>('/diagrams/ai-edit', body, options);
  }

  // -------------------------
  // Versions
  // -------------------------
  readVersions(diagramId: UUID, options: ApiRequestOptions = {}) {
    return this.api.get<Version[]>(`/versions/${diagramId}`, options);
  }

  restoreVersion(versionId: UUID, options: ApiRequestOptions = {}) {
    return this.api.post<Diagram>(`/versions/${versionId}/restore`, options);
  }

  // -------------------------
  // Sharing
  // -------------------------
  listShareLinks(diagramId: UUID, options: ApiRequestOptions = {}) {
    return this.get<ShareList>('/sharing/', { diagram_id: diagramId }, options);
  }

  createShareLink(body: ShareCreate, options: ApiRequestOptions = {}) {
    return this.post<ShareLink>('/sharing/', body, options);
  }

  refreshShareLinks(diagramId: UUID, options: ApiRequestOptions = {}) {
    return this.post<ShareList>('/sharing/refresh', { diagram_id: diagramId }, options);
  }

  updateShareLink(token: string, body: ShareUpdate, options: ApiRequestOptions = {}) {
    return this.patch<ShareLink>(`/sharing/${token}`, body, options);
  }

  deleteShareLink(token: string, diagramId: UUID, options: ApiRequestOptions = {}) {
    return this.delete<{ status: string }>(`/sharing/${token}`, { ...options, query: { diagram_id: diagramId } });
  }

  deleteDiagramShare(diagramId: UUID, options: ApiRequestOptions = {}) {
    return this.delete<ShareRevokeResponse>(`/diagrams/${diagramId}/share`, options);
  }

  // -------------------------
  // Archive
  // -------------------------
  readArchivedProjects(
    params?: {
      skip?: number;
      limit?: number;
      sort_by?: ProjectSortBy | 'archived_at';
      sort_order?: SortOrder;
    },
    options: Omit<ApiRequestOptions, 'query'> = {},
  ) {
    return this.get<PaginatedResponse<Project>>('/archive/projects', params, options);
  }

  archiveProject(id: UUID, options: ApiRequestOptions = {}) {
    return this.post<Project>(`/archive/projects/${id}`, {}, options);
  }

  restoreArchivedProject(id: UUID, name?: string, options: ApiRequestOptions = {}) {
    return this.post<Project>(`/archive/projects/${id}/restore`, name ? { name } : {}, options);
  }

  deleteArchivedProject(id: UUID, options: ApiRequestOptions = {}) {
    return this.delete<{ status: string }>(`/archive/projects/${id}`, options);
  }

  archiveFolder(id: UUID, options: ApiRequestOptions = {}) {
    return this.post<Folder>(`/archive/folders/${id}`, {}, options);
  }

  restoreArchivedFolder(id: UUID, name?: string, options: ApiRequestOptions = {}) {
    return this.post<Folder>(`/archive/folders/${id}/restore`, name ? { name } : {}, options);
  }

  deleteArchivedFolder(id: UUID, options: ApiRequestOptions = {}) {
    return this.delete<{ status: string }>(`/archive/folders/${id}`, options);
  }

  archiveDiagram(id: UUID, options: ApiRequestOptions = {}) {
    return this.post<Diagram>(`/archive/diagrams/${id}`, {}, options);
  }

  restoreArchivedDiagram(id: UUID, name?: string, options: ApiRequestOptions = {}) {
    return this.post<Diagram>(`/archive/diagrams/${id}/restore`, name ? { name } : {}, options);
  }

  deleteArchivedDiagram(id: UUID, options: ApiRequestOptions = {}) {
    return this.delete<{ status: string }>(`/archive/diagrams/${id}`, options);
  }

  readArchivedDiagrams(
    params?: {
      skip?: number;
      limit?: number;
      sort_by?: DiagramSortBy;
      sort_order?: SortOrder;
    },
    options: Omit<ApiRequestOptions, 'query'> = {},
  ) {
    return this.get<PaginatedResponse<Diagram>>('/archive/diagrams', params, options);
  }

  // -------------------------
  // Search
  // -------------------------
  search(
    params: { query: string; type: SearchEntityType },
    options: Omit<ApiRequestOptions, 'query'> = {},
  ) {
    return this.get<SearchResult[]>('/search/', { query: params.query, type: params.type }, options);
  }

  protected get<T>(
    path: string,
    query?: QueryParams,
    options: Omit<ApiRequestOptions, 'query'> = {},
  ) {
    return this.api.get<T>(path, { ...options, query });
  }

  protected post<T>(path: string, json?: unknown, options: ApiRequestOptions = {}) {
    return this.api.post<T>(path, { ...options, json });
  }

  protected put<T>(path: string, json?: unknown, options: ApiRequestOptions = {}) {
    return this.api.put<T>(path, { ...options, json });
  }

  protected patch<T>(path: string, json?: unknown, options: ApiRequestOptions = {}) {
    return this.api.patch<T>(path, { ...options, json });
  }

  protected delete<T>(path: string, options: ApiRequestOptions = {}) {
    return this.api.delete<T>(path, options);
  }

  protected multipart<T>(
    path: string,
    form: FormData,
    options: Omit<ApiRequestOptions, 'body' | 'json'> = {},
  ) {
    return this.api.multipart<T>(path, form, options);
  }
}

/**
 * `ApiClient.appendQuery` skips `null`/`undefined`. Backend treats omitted
 * `folder_id` as all diagrams, and `""` / `"null"` as uncategorized.
 */
function registerPath(token: string): string {
  return `/auth/register/${encodeURIComponent(token)}`;
}

function stepUp(currentPassword?: string): { current_password?: string } {
  const value = currentPassword?.trim();
  return value ? { current_password: value } : {};
}

function diagramsQuery(params?: {
  folder_id?: UUID | null;
  skip?: number;
  limit?: number;
  sort_by?: DiagramSortBy;
  sort_order?: SortOrder;
  status?: ArchiveListStatus;
}): QueryParams | undefined {
  if (!params) return undefined;
  const query: QueryParams = {};
  if (params.skip !== undefined) query['skip'] = params.skip;
  if (params.limit !== undefined) query['limit'] = params.limit;
  if (params.sort_by !== undefined) query['sort_by'] = params.sort_by;
  if (params.sort_order !== undefined) query['sort_order'] = params.sort_order;
  if (params.status !== undefined) query['status'] = params.status;
  if (params.folder_id !== undefined) {
    query['folder_id'] = params.folder_id === null ? '' : params.folder_id;
  }
  return query;
}
